import React, { useEffect, useState, useMemo, useRef } from "react";
import { toast } from "react-toastify";
import {
	Autocomplete,
	Card,
	Checkbox,
	FormControlLabel,
	TextField,
	Link,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ValidatedMobileDatePicker } from "../ValidatedMobileDatePicker/ValidatedMobileDatePicker";
import { MDBox } from "../MDBox";
import colors from "../../../../assets/theme/base/colors";
import { IFilter, IFilterValue, FilterType } from "../DataTable/Filters";

export type FilterValues = Record<string, any>;

export interface IFilterBarProps {
	filters: IFilter[];
	onChange: (filterValues: FilterValues) => void;
	hideClearFilters?: boolean;
	disableCard?: boolean;
}

const getDefaultValue = (type: FilterType, propname: string): Record<string, any> => {
	switch (type) {
		case FilterType.multiselect:
			return { [propname]: [] };
		case FilterType.select:
			return { [propname]: null };
		case FilterType.checkbox:
			return { [propname]: false };
		case FilterType.dateRange:
			return { [`${propname}Start`]: null, [`${propname}End`]: null };
		case FilterType.date:
			return { [propname]: null };
		case FilterType.text:
			return { [propname]: "" };
		default:
			return { [propname]: null };
	}
};

const buildDefaults = (filters: IFilter[]): FilterValues => {
	const defaults: FilterValues = {};
	filters.forEach((f) => {
		Object.assign(defaults, getDefaultValue(f.type, f.propname));
	});
	return defaults;
};

export const FilterBar: React.FC<IFilterBarProps> = ({ filters, onChange, hideClearFilters, disableCard }) => {
	const [filterValues, setFilterValues] = useState<FilterValues>(() => buildDefaults(filters));
	const [optionsCache, setOptionsCache] = useState<Record<string, IFilterValue[]>>({});
	const [loadingOptions, setLoadingOptions] = useState<Record<string, boolean>>({});
	const onChangeRef = useRef(onChange);
	onChangeRef.current = onChange;
	const pendingOnChange = useRef<FilterValues | null>(null);

	// Load async options on mount
	useEffect(() => {
		filters.forEach((filter) => {
			if (filter.optionsLoader && !optionsCache[filter.propname]) {
				setLoadingOptions((prev) => ({ ...prev, [filter.propname]: true }));
				filter
					.optionsLoader()
					.then((options) => {
						setOptionsCache((prev) => ({ ...prev, [filter.propname]: options }));
					})
					.catch(() => {
						toast.error(`Failed to load options for ${filter.label}`);
					})
					.finally(() => {
						setLoadingOptions((prev) => ({ ...prev, [filter.propname]: false }));
					});
			}
		});
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (pendingOnChange.current !== null) {
			onChangeRef.current(pendingOnChange.current);
			pendingOnChange.current = null;
		}
	}, [filterValues]);

	const handleChange = (propname: string, value: any) => {
		setFilterValues((prev) => {
			const next = { ...prev, [propname]: value };
			pendingOnChange.current = next;
			return next;
		});
	};

	const handleDateRangeChange = (propname: string, start: Date | null, end: Date | null) => {
		setFilterValues((prev) => {
			const next = {
				...prev,
				[`${propname}Start`]: start,
				[`${propname}End`]: end,
			};
			pendingOnChange.current = next;
			return next;
		});
	};

	const hasActiveFilters = useMemo(() => {
		return Object.keys(filterValues).some((key) => {
			const value = filterValues[key];
			if (value === null || value === undefined) return false;
			if (typeof value === "string" && value === "") return false;
			if (typeof value === "boolean" && value === false) return false;
			if (Array.isArray(value) && value.length === 0) return false;
			return true;
		});
	}, [filterValues]);

	const clearFilters = () => {
		const defaults = buildDefaults(filters);
		setFilterValues(defaults);
		onChangeRef.current(defaults);
	};

	const getOptions = (filter: IFilter): IFilterValue[] => {
		return filter.values || optionsCache[filter.propname] || [];
	};

	const renderMultiselect = (filter: IFilter) => {
		const options = getOptions(filter);
		const loading = loadingOptions[filter.propname] || false;
		const value = (filterValues[filter.propname] || []) as IFilterValue[];

		return (
			<Autocomplete
				multiple
				size="small"
				sx={{ minWidth: filter.minWidth || 220 }}
				options={options}
				value={value}
				getOptionLabel={(option) => option.key}
				isOptionEqualToValue={(option, val) => option.val === val.val}
				onChange={(_, newValues) => handleChange(filter.propname, newValues)}
				disableCloseOnSelect
				disabled={loading}
				loading={loading}
				ListboxProps={{ style: { padding: 0 } }}
				renderOption={(props, option, { selected }) => (
					<li {...props} style={{ padding: "4px 4px", minHeight: "unset" }}>
						<Checkbox
							icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
							checkedIcon={<CheckBoxIcon fontSize="small" />}
							style={{ marginRight: 8, padding: 0 }}
							checked={selected}
						/>
						{option.key}
					</li>
				)}
				renderInput={(params) => <TextField {...params} label={filter.label} />}
			/>
		);
	};

	const renderSelect = (filter: IFilter) => {
		const options = getOptions(filter);
		const loading = loadingOptions[filter.propname] || false;
		const value = (filterValues[filter.propname] as IFilterValue | null) ?? null;

		return (
			<Autocomplete
				size="small"
				sx={{ minWidth: filter.minWidth || 220 }}
				options={options}
				value={value}
				getOptionLabel={(option) => option.key}
				isOptionEqualToValue={(option, val) => option.val === val.val}
				onChange={(_, newValue) => handleChange(filter.propname, newValue)}
				disabled={loading}
				loading={loading}
				ListboxProps={{ style: { padding: 0 } }}
				renderOption={(props, option) => (
					<li {...props} style={{ padding: "4px 4px", minHeight: "unset" }}>
						{option.key}
					</li>
				)}
				renderInput={(params) => <TextField {...params} label={filter.label} />}
			/>
		);
	};

	const renderCheckbox = (filter: IFilter) => {
		const value = (filterValues[filter.propname] || false) as boolean;

		return (
			<FormControlLabel
				control={
					<Checkbox
						checked={value}
						onChange={(e) => handleChange(filter.propname, e.target.checked)}
						size="small"
					/>
				}
				label={filter.label}
			/>
		);
	};

	const renderDateRange = (filter: IFilter) => {
		const dateFrom = (filterValues[`${filter.propname}Start`] as Date | null) ?? null;
		const dateTo = (filterValues[`${filter.propname}End`] as Date | null) ?? null;

		return (
			<LocalizationProvider dateAdapter={AdapterDateFns}>
				<ValidatedMobileDatePicker
					inputFormat="yyyy/MM/dd"
					mask="____/__/__"
					label={`${filter.label} From`}
					value={dateFrom}
					maxDate={dateTo}
					onChange={(newValue: any) => handleDateRangeChange(filter.propname, newValue, dateTo)}
					renderInput={(params: any) => (
						<TextField {...params} size="small" sx={{ minWidth: filter.minWidth || 170 }} />
					)}
					views={["year", "month", "day"]}
				/>
				<ValidatedMobileDatePicker
					inputFormat="yyyy/MM/dd"
					mask="____/__/__"
					label={`${filter.label} To`}
					value={dateTo}
					minDate={dateFrom}
					onChange={(newValue: any) => handleDateRangeChange(filter.propname, dateFrom, newValue)}
					renderInput={(params: any) => (
						<TextField {...params} size="small" sx={{ minWidth: filter.minWidth || 170 }} />
					)}
					views={["year", "month", "day"]}
				/>
			</LocalizationProvider>
		);
	};

	const renderDate = (filter: IFilter) => {
		const value = (filterValues[filter.propname] as Date | null) ?? null;

		return (
			<LocalizationProvider dateAdapter={AdapterDateFns}>
				<ValidatedMobileDatePicker
					inputFormat="yyyy/MM/dd"
					mask="____/__/__"
					label={filter.label}
					value={value}
					onChange={(newValue: any) => handleChange(filter.propname, newValue)}
					renderInput={(params: any) => (
						<TextField {...params} size="small" sx={{ minWidth: filter.minWidth || 170 }} />
					)}
					views={["year", "month", "day"]}
				/>
			</LocalizationProvider>
		);
	};

	const renderText = (filter: IFilter) => {
		const value = (filterValues[filter.propname] || "") as string;

		return (
			<TextField
				size="small"
				label={filter.label}
				value={value}
				onChange={(e) => handleChange(filter.propname, e.target.value)}
				sx={{ minWidth: filter.minWidth || 220 }}
			/>
		);
	};

	const renderFilter = (filter: IFilter) => {
		switch (filter.type) {
			case FilterType.multiselect:
				return renderMultiselect(filter);
			case FilterType.select:
				return renderSelect(filter);
			case FilterType.checkbox:
				return renderCheckbox(filter);
			case FilterType.dateRange:
				return renderDateRange(filter);
			case FilterType.date:
				return renderDate(filter);
			case FilterType.text:
				return renderText(filter);
			default:
				return null;
		}
	};

	const content = (
		<MDBox display="flex" flexWrap="wrap" gap={2} alignItems="center">
			{filters.map((filter) => (
				<React.Fragment key={filter.propname}>
					{renderFilter(filter)}
				</React.Fragment>
			))}

			{!hideClearFilters && hasActiveFilters && (
				<Link
					component="button"
					onClick={clearFilters}
					sx={{
						ml: "auto",
						fontSize: "0.875rem",
						color: colors.primary.main,
					}}
					underline="hover"
				>
					Clear filters
				</Link>
			)}
		</MDBox>
	);

	return disableCard ? content : <Card sx={{ px: 2, py: 1.5 }}>{content}</Card>;
};
